import { LayerData, addPointModeAtom, featureNavigationAtom, selectedFeatureAtom, tLayerSettings } from '@/atoms';
import { aciToRgb, colours, computeMapOrigin, parseOgrStyle, toThreeMeshBasicParams } from '@/core';
import { NumberWithUnit, toDreiTextProps } from '@/core/style';
import { FeaturesQuery } from '@/graphql/graphql';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useTheme } from '@/hooks/useTheme';
import { PivotControls, Text } from '@react-three/drei';
import { useThree, type ThreeEvent } from '@react-three/fiber';
import { useAtom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { Suspense, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as THREE from 'three';

// Type definitions for GeoJSON-like geometry
type Geometry =
  | { type: 'Point'; coordinates: [number, number, number?] }
  | { type: 'MultiPoint'; coordinates: [number, number, number?][] }
  | { type: 'LineString'; coordinates: [number, number, number?][] }
  | { type: 'MultiLineString'; coordinates: [number, number, number?][][] }
  | { type: 'Polygon'; coordinates: [number, number, number?][][] }
  | { type: 'MultiPolygon'; coordinates: [number, number, number?][][][] }
  | { type: 'GeometryCollection'; geometries: Geometry[] };

const EPS = 1e-9;
const uncloseRing = (ring: [number, number, number][]) => {
  if (ring.length < 2) return ring;
  const [x0, y0] = ring[0];
  const [xn, yn] = ring[ring.length - 1];
  return Math.abs(x0 - xn) < EPS && Math.abs(y0 - yn) < EPS ? ring.slice(0, -1) : ring;
};

export const GeoJson = ({
  features,
  visible,
  flying,
  layers,
}: {
  features: FeaturesQuery['featuresByLayer'][number][];
  visible?: boolean;
  flying: boolean;
  layers: LayerData[] | null;
}) => {
  const selectedFeature = useAtomValue(selectedFeatureAtom);
  const [navigationContext, setNavigationContext] = useAtom(featureNavigationAtom);
  const camera = useThree((state) => state.camera);
  const mapOrigin = useMemo(() => (features ? computeMapOrigin(features) : new THREE.Vector3(0, 0, 0)), [features]);
  const mapOriginArray = useMemo(() => mapOrigin.toArray(), [mapOrigin]);
  const [params] = useSearchParams();
  const { createUpdateHandler } = useFeatureActions();
  const layerSettings = useLayerSettingsMany(layers?.map((l) => l.id) || []);
  const addPointMode = useAtomValue(addPointModeAtom);

  // cleanup
  useEffect(() => {
    if (!selectedFeature && navigationContext) {
      setNavigationContext(null);
    }
  }, [selectedFeature, navigationContext, setNavigationContext]);

  // Filter features within 35 meters of camera
  const nearbyFeatures = useMemo(() => {
    if (!features) return [];
    if (flying) return features;

    const cameraPosition = camera.position.clone();
    const maxDistance = 35; // meters

    return features.filter((feature) => {
      const { geometry } = feature;
      if (!geometry) return false;

      // Helper function to check if any point in an array is within distance
      const isAnyPointWithinDistance = (coordinates: [number, number, number?][]): boolean => {
        return coordinates.some((coord) => {
          const point = new THREE.Vector3(...coord);
          return cameraPosition.distanceTo(point) <= maxDistance;
        });
      };

      // Helper function to check if any point in nested arrays is within distance
      const isAnyPointInNestedArrayWithinDistance = (coordinates: [number, number, number?][][]): boolean => {
        return coordinates.some((coordArray) => isAnyPointWithinDistance(coordArray));
      };

      // Check if any point of the geometry is within distance
      switch (geometry.type) {
        case 'Point': {
          const point = new THREE.Vector3(...geometry.coordinates);
          return cameraPosition.distanceTo(point) <= maxDistance;
        }
        case 'MultiPoint': {
          return isAnyPointWithinDistance(geometry.coordinates);
        }
        case 'LineString': {
          return isAnyPointWithinDistance(geometry.coordinates);
        }
        case 'MultiLineString': {
          return isAnyPointInNestedArrayWithinDistance(geometry.coordinates);
        }
        case 'Polygon': {
          // Check all rings (outer + holes)
          return isAnyPointInNestedArrayWithinDistance(geometry.coordinates);
        }
        case 'MultiPolygon': {
          // Check all polygons and their rings
          return geometry.coordinates.some((polygon: [number, number, number?][][]) =>
            isAnyPointInNestedArrayWithinDistance(polygon),
          );
        }
        case 'GeometryCollection': {
          // Recursively check all geometries in the collection
          return geometry.geometries.some((subGeometry: Geometry) => {
            // Inline check for sub-geometry
            switch (subGeometry.type) {
              case 'Point': {
                const point = new THREE.Vector3(...subGeometry.coordinates);
                return cameraPosition.distanceTo(point) <= maxDistance;
              }
              case 'MultiPoint':
                return isAnyPointWithinDistance(subGeometry.coordinates);
              case 'LineString':
                return isAnyPointWithinDistance(subGeometry.coordinates);
              case 'MultiLineString':
                return isAnyPointInNestedArrayWithinDistance(subGeometry.coordinates);
              case 'Polygon':
                return isAnyPointInNestedArrayWithinDistance(subGeometry.coordinates);
              case 'MultiPolygon':
                return subGeometry.coordinates.some((polygon: [number, number, number?][][]) =>
                  isAnyPointInNestedArrayWithinDistance(polygon),
                );
              default:
                return false;
            }
          });
        }
        default:
          return false;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, flying, camera.position.x.toFixed(5), camera.position.y.toFixed(5), camera.position.z.toFixed(5)]);

  const filterNavigablePointFeatures = useCallback(
    (features: FeaturesQuery['featuresByLayer'][number][], excludeFeatureId?: string) => {
      return features.filter((f) => {
        // Only include Point features
        if (f.geometry?.type !== 'Point') return false;

        // Exclude the feature itself if specified
        if (excludeFeatureId && f.id === excludeFeatureId) return false;

        // Exclude features with z=0
        const z = f.geometry.coordinates[2] || 0;
        if (z === 0) return false;

        // Exclude text features
        if (f.properties.Text) return false;

        return true;
      });
    },
    [],
  );

  const calculateDistance = useCallback(
    (feature1: FeaturesQuery['featuresByLayer'][number], feature2: FeaturesQuery['featuresByLayer'][number]) => {
      if (feature1.geometry?.type !== 'Point' || feature2.geometry?.type !== 'Point') {
        return Infinity;
      }

      const point1 = new THREE.Vector3(...feature1.geometry.coordinates);
      const point2 = new THREE.Vector3(...feature2.geometry.coordinates);

      return point1.distanceTo(point2);
    },
    [],
  );

  const createNavigationContext = useCallback(
    (
      selectedFeature: FeaturesQuery['featuresByLayer'][number],
      allFeatures: FeaturesQuery['featuresByLayer'][number][],
      cameraPos: THREE.Vector3,
    ) => {
      // Only create context for Point features
      if (selectedFeature.geometry?.type !== 'Point') {
        return null;
      }

      // Filter to navigable Point features (excluding the selected one)
      const otherNavigableFeatures = filterNavigablePointFeatures(allFeatures, selectedFeature.id);

      const maxDistance = 0.2;
      const nearbyNavigableFeatures = otherNavigableFeatures.filter((feature) => {
        const distance = calculateDistance(selectedFeature, feature);
        return distance <= maxDistance;
      });

      // Sort by distance from selected feature
      const sortedFeatures = nearbyNavigableFeatures.sort((a, b) => {
        const distA = calculateDistance(selectedFeature, a);
        const distB = calculateDistance(selectedFeature, b);
        return distA - distB;
      });

      // Include the selected feature at the beginning
      const allNavigableFeatures = [selectedFeature, ...sortedFeatures];

      // Only create context if there are at least 2 features
      if (allNavigableFeatures.length < 2) {
        return null;
      }

      return {
        featureIds: allNavigableFeatures.map((f) => f.id),
        currentIndex: 0, // Selected feature is always at index 0
        cameraPosition: cameraPos.toArray() as [number, number, number],
        layerId: selectedFeature.layerId!,
      };
    },
    [filterNavigablePointFeatures, calculateDistance],
  );

  // Handle navigation context index changes to update selected feature
  const updateSelectedFeatureFromNavigation = useAtomCallback(
    useCallback(
      (get, set) => {
        const navContext = get(featureNavigationAtom);
        const currentSelected = get(selectedFeatureAtom);

        if (!navContext || !currentSelected) return;

        // Get the feature ID from the current index
        const targetFeatureId = navContext.featureIds[navContext.currentIndex];

        // If the selected feature already matches, no need to update
        if (currentSelected.id === targetFeatureId) return;

        // Find the feature in nearbyFeatures
        const feature = nearbyFeatures.find((f) => f.id === targetFeatureId);
        if (feature) {
          // Update selected feature with full feature object
          set(selectedFeatureAtom, {
            ...feature,
            onSave: createUpdateHandler(feature.id, feature.layerId!),
          });
        }
      },
      [nearbyFeatures, createUpdateHandler],
    ),
  );

  // Watch for navigation context index changes
  useEffect(() => {
    updateSelectedFeatureFromNavigation();
  }, [navigationContext?.currentIndex, updateSelectedFeatureFromNavigation]);

  const handleClick = useAtomCallback(
    useCallback(
      (get, set, event: ThreeEvent<MouseEvent>) => {
        event.stopPropagation();
        if (addPointMode.enabled) return false;
        if (visible === false) return false;
        if (event.delta > 3) return false;
        if (params.get('showMeasurements') === 'true') return false;

        const existingSelectedFeature = get(selectedFeatureAtom);
        const existingNavigationContext = get(featureNavigationAtom);

        // If a feature is already selected with navigation context, don't handle the click
        if (existingSelectedFeature && existingNavigationContext) return false;

        const { object } = event;
        const featureId = object.userData.featureId;
        const feature = nearbyFeatures.find((f) => f.id === featureId);
        if (feature) {
          set(selectedFeatureAtom, {
            ...feature,
            onSave: createUpdateHandler(feature.id, feature.layerId!),
          });

          // Create navigation context for Point features
          const navigationContext = createNavigationContext(feature, nearbyFeatures, camera.position);
          set(featureNavigationAtom, navigationContext);
        }
      },
      [
        visible,
        nearbyFeatures,
        params,
        createUpdateHandler,
        createNavigationContext,
        camera.position,
        addPointMode.enabled,
      ],
    ),
  );

  return (
    <group visible={visible}>
      {nearbyFeatures.map((_feature) => {
        const { geometry } = _feature;
        if (!geometry) return null;

        const feature = selectedFeature && selectedFeature?.id === _feature.id ? selectedFeature : _feature;
        const isSelected = Boolean(selectedFeature && selectedFeature?.id === feature.id);

        return isSelected && selectedFeature?.isMoving && feature.geometry.type === 'Point' ? (
          <Move3DArrows
            position={new THREE.Vector3(...feature.geometry.coordinates)}
            key={`move-${selectedFeature.id}`}>
            <FeatureElement
              feature={feature}
              key={feature.id}
              mapOrigin={mapOrigin}
              mapOriginArray={mapOriginArray}
              onClick={handleClick}
              selected={isSelected}
              layerSettings={layerSettings[feature.layerId as string]}
            />
          </Move3DArrows>
        ) : (
          <FeatureElement
            feature={feature}
            key={feature.id}
            mapOrigin={mapOrigin}
            mapOriginArray={mapOriginArray}
            onClick={handleClick}
            selected={isSelected}
            layerSettings={layerSettings[feature.layerId as string]}
          />
        );
      })}
    </group>
  );
};

const FeatureElement: React.FC<{
  feature: FeaturesQuery['featuresByLayer'][number];
  mapOrigin: THREE.Vector3;
  mapOriginArray: [number, number, number];
  onClick: (event: ThreeEvent<MouseEvent>) => void;
  selected: boolean;
  layerSettings?: tLayerSettings;
}> = memo(({ feature, mapOrigin, mapOriginArray, onClick, selected, layerSettings }) => {
  const theme = useTheme();
  const { geometry } = feature;
  const userData = useMemo(
    () => ({
      type: geometry.type,
      layerId: feature.layerId,
      featureId: feature.id,
    }),
    [geometry.type, feature.layerId, feature.id],
  );

  const ogrStyle = useMemo(() => parseOgrStyle(feature.properties.OGR_STYLE || ''), [feature.properties.OGR_STYLE]);
  const styling = useMemo(() => {
    let styling: THREE.MeshBasicMaterialParameters = {
      color:
        layerSettings && layerSettings.vectorDefaultColor !== '#000000'
          ? layerSettings.vectorDefaultColor
          : theme === 'light'
            ? colours.white.lightmode
            : colours.white.darkmode,
    };
    if (feature.properties.OGR_STYLE) styling = { ...toThreeMeshBasicParams(ogrStyle) };
    if (feature.properties.CadColor) styling = { ...styling, color: aciToRgb(feature.properties.CadColor || 0) };

    if (feature.properties.TransP)
      styling = { ...styling, opacity: feature.properties.TransP / 100, transparent: true };
    else if (layerSettings && layerSettings.vectorDefaultOpacity < 1.0)
      styling = { ...styling, opacity: layerSettings.vectorDefaultOpacity, transparent: true };

    if (layerSettings && layerSettings.vectorIsWireframe) styling.wireframe = true;

    if (selected) styling = { ...styling, color: colours.geojson.active };

    return styling;
  }, [
    feature.properties.CadColor,
    feature.properties.OGR_STYLE,
    feature.properties.TransP,
    ogrStyle,
    selected,
    layerSettings,
  ]);

  // Pre-calculate commonly used values
  const isPoint = geometry.type === 'Point';
  const pointCoords = isPoint ? geometry.coordinates : null;
  const dx = isPoint && pointCoords ? pointCoords[0] - mapOrigin.x : 0;
  const dy = isPoint && pointCoords ? pointCoords[1] - mapOrigin.y : 0;
  const dz = isPoint && pointCoords ? (pointCoords[2] || 0) - mapOrigin.z : 0;

  // Memoize sphere geometry for Point geometries
  const sphereGeometry = useMemo(() => {
    if (!isPoint || !pointCoords) return null;

    let size = layerSettings?.vectorDefaultPointSize || 0.15;
    if (ogrStyle.parts[0]?.params.find((param) => param.name === 'w' && (param.value as NumberWithUnit).unit === 'g'))
      size = (
        ogrStyle.parts[0].params.find((param) => param.name === 'w' && (param.value as NumberWithUnit).unit === 'g')
          ?.value as NumberWithUnit
      ).value;

    if (feature.properties.SubClasses === 'AcDbEntity:AcDbBlockReference') size = 0.01;

    // todo: cad point size handling Balint?
    if (feature.properties.EntityHandle) size = 0.01;

    const geo = new THREE.SphereGeometry(size, 16, 16);
    geo.translate(dx, dy, dz);
    return geo;
  }, [
    isPoint,
    pointCoords,
    dx,
    dy,
    dz,
    ogrStyle.parts,
    feature.properties.SubClasses,
    layerSettings?.vectorDefaultPointSize,
  ]);

  switch (geometry.type) {
    case 'Point': {
      const z = geometry.coordinates[2] || 0;
      if (z === 0) return null;

      if (feature.properties.Text && feature.properties.OGR_STYLE) {
        const textProps = toDreiTextProps(parseOgrStyle(feature.properties.OGR_STYLE), {
          fallbackText: feature.properties.Text,
        });
        return (
          <Suspense fallback={null}>
            {/* <Billboard follow={true}> */}
            <Text {...textProps} position={geometry.coordinates} material-transparent material-depthTest={false}>
              {textProps.text || ''}
            </Text>
            {/* </Billboard> */}
          </Suspense>
        );
      }

      if (!sphereGeometry) return null;

      return (
        <>
          <mesh onClick={onClick} userData={userData} geometry={sphereGeometry} position={mapOriginArray}>
            <meshBasicMaterial {...styling} />
          </mesh>
        </>
      );
    }

    case 'MultiPoint': {
      return geometry.coordinates.map(([x, y, z = 0], i) => {
        const dx = x - mapOrigin.x;
        const dy = y - mapOrigin.y;
        const dz = z - mapOrigin.z;
        if (z === 0) return null;
        const sphereGeo = new THREE.SphereGeometry(0.15, 16, 16);
        sphereGeo.translate(dx, dy, dz);
        return (
          <mesh
            key={`${feature.id}-${i}`}
            geometry={sphereGeo}
            onClick={handleClick}
            userData={userData}
            position={mapOriginArray}>
            <meshBasicMaterial {...styling} />
          </mesh>
        );
      });
    }

    case 'LineString': {
      if (geometry.coordinates.every((coords) => coords[2] === 0)) return null;
      const points = geometry.coordinates.map(
        ([x, y, z = 0]) => new THREE.Vector3(x - mapOrigin.x, y - mapOrigin.y, z - mapOrigin.z),
      );
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      return (
        <line geometry={lineGeometry} onClick={onClick} userData={userData} position={mapOriginArray}>
          <lineBasicMaterial {...styling} />
        </line>
      );
    }

    case 'MultiLineString': {
      if (geometry.coordinates.every((coords) => coords[2] === 0)) return null;
      return geometry.coordinates
        .map((lineCoords: any, i: number) => {
          const points = lineCoords.map(
            ([x, y, z = 0]) => new THREE.Vector3(x - mapOrigin.x, y - mapOrigin.y, z - mapOrigin.z),
          );
          const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
          return (
            <line
              key={`${feature.id}-${i}`}
              geometry={lineGeometry}
              position={mapOriginArray}
              onClick={onClick}
              userData={userData}>
              <lineBasicMaterial {...styling} />
            </line>
          );
        })
        .filter(Boolean);
    }

    case 'Polygon':
    case 'MultiPolygon': {
      const polys = geometry.type === 'Polygon' ? [geometry.coordinates] : (geometry.coordinates as any[]);

      if (polys.some((polyCoords) => !polyCoords[0])) return null;
      if (polys.every((polyCoords) => polyCoords[0].every((coords: number[]) => coords[2] === 0))) return null;

      // todo: if this is slow, all feature rendering calculations shall be put into a useMemo
      return polys.map((polyCoords: any, i: number) => {
        // 1) Open rings (assumes backend already: largest ring first = exterior, holes follow; winding fixed)
        const [exteriorRing, ...holeRings] = (polyCoords as [number, number, number][][]).map(uncloseRing);

        // 2) Build 2D vectors for triangulation (OPEN rings)
        const toV2 = ([x, y]: [number, number]) => new THREE.Vector2(x - mapOrigin.x, y - mapOrigin.y);
        const exterior2 = exteriorRing.map(([x, y]) => toV2([x, y]));
        const holes2 = holeRings.map((hole) => hole.map(([x, y]) => toV2([x, y])));

        // 3) Triangulate
        const faces = THREE.ShapeUtils.triangulateShape(exterior2, holes2);

        // 4) Build 3D vertex buffer in the same concatenation order (OPEN rings)
        const allRingsOpen = [exteriorRing, ...holeRings];
        const vertCount = allRingsOpen.reduce((n, r) => n + r.length, 0);
        const positions = new Float32Array(vertCount * 3);

        let k = 0;
        for (const ring of allRingsOpen) {
          for (const [x, y, z = 0] of ring) {
            positions[k++] = x - mapOrigin.x;
            positions[k++] = y - mapOrigin.y;
            positions[k++] = z - mapOrigin.z;
          }
        }

        const indices = Uint32Array.from(faces.flat());

        const polyGeo = new THREE.BufferGeometry();
        polyGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        polyGeo.setIndex(new THREE.BufferAttribute(indices, 1));
        polyGeo.translate(0, 0, 0.0001 * Math.random());

        return (
          <mesh
            onClick={onClick}
            userData={userData}
            key={`${feature.id}-${i}`}
            geometry={polyGeo}
            position={mapOriginArray}>
            <meshBasicMaterial {...styling} side={THREE.DoubleSide} />
          </mesh>
        );
      });
    }

    case 'GeometryCollection': {
      const geometryCollection = geometry as { type: 'GeometryCollection'; geometries: Geometry[] };
      return geometryCollection.geometries
        .map((geometry: Geometry, i: number) => {
          // Create a temporary feature with the individual geometry
          const tempFeature = {
            ...feature,
            geometry: geometry,
          };
          return (
            <FeatureElement
              key={`${feature.id}-geom-${i}`}
              feature={tempFeature}
              mapOrigin={mapOrigin}
              mapOriginArray={mapOriginArray}
              onClick={onClick}
              selected={selected}
              layerSettings={layerSettings}
            />
          );
        })
        .filter(Boolean);
    }

    default:
      return null;
  }
});

FeatureElement.displayName = 'FeatureElement';

const Move3DArrows: React.FC<React.PropsWithChildren<{ position: THREE.Vector3 }>> = ({
  children,
  position: defaultPosition,
}) => {
  const [position, setPosition] = useState<THREE.Vector3>(new THREE.Vector3(0, 0, 0));
  const [selectedFeature, setSelectedFeature] = useAtom(selectedFeatureAtom);
  const [matrix, setMatrix] = useState<THREE.Matrix4>(new THREE.Matrix4());

  useEffect(() => {
    setPosition(new THREE.Vector3().copy(defaultPosition));
    const m = new THREE.Matrix4();
    m.setPosition(defaultPosition.x, defaultPosition.y, defaultPosition.z);
    setMatrix(m);
  }, [defaultPosition.x, defaultPosition.y, defaultPosition.z]);

  const delta = useMemo(
    () =>
      new THREE.Vector3(position.x - defaultPosition.x, position.y - defaultPosition.y, position.z - defaultPosition.z),
    [position, defaultPosition],
  );

  return (
    <group>
      <PivotControls
        matrix={matrix}
        visible
        autoTransform={false}
        disableRotations
        disableScaling
        onDrag={(_l, __, w: THREE.Matrix4) => {
          const position = new THREE.Vector3();
          const rotation = new THREE.Quaternion();
          w.decompose(position, rotation, new THREE.Vector3());
          setMatrix(w);
          setPosition(position);
        }}
        onDragEnd={() => {
          if (!selectedFeature) return;

          setSelectedFeature({
            ...selectedFeature,
            isEditing: true,
            geometry: {
              ...selectedFeature.geometry,
              coordinates: [position.x, position.y, position.z],
            },
          });
        }}></PivotControls>
      <group position={delta}>{children}</group>
    </group>
  );
};
